/*
 * SBV persistent stock cap note on a cart line.
 *
 * Adrian's request: "keep the stock-limit explanation visible after refresh".
 * The event-time message in the drawer error slot is correct but it dies on
 * the next render. This file re-renders it as a quiet note on the line
 * itself, for as long as the line is still sitting at the cap.
 *
 * Where the number comes from: assets/sbv-cart-sync.js writes it. When
 * Shopify answers a cart write with 422 it has already capped the line, so
 * the cart's own post-422 count for that variant IS the cap. It is recorded
 * under the variant id in sessionStorage['sbv-stock-caps'] as
 * { cap, message, ts, others }. No Liquid number is used anywhere, because none of
 * them match Shopify's own message (Liquid reports 25 on bundle variant
 * 71191880958205 while the 422 caps the line at 10), and the note must never
 * disagree with the message the rider just read.
 *
 * What it renders, per the build spec section 4:
 *   - Drawer: full width directly under .cart-drawer-item__quantity-and-prices,
 *     id CartDrawer-StockNote-<line index>.
 *   - Cart page: inside td.cart-item__quantity, between the quantity wrapper
 *     and the vendor's error slot, id StockNote-<line index>, max-width 24rem.
 *   - Copy: "<cap> available, the maximum for this order."
 *   - aria-describedby on that line's quantity input, appended, never
 *     overwriting what is already there. No role, no live region, and never
 *     the vendor's red error slot.
 *   - max on the quantity input and aria-disabled on the plus button, on the
 *     same line. form#cart and #CartDrawer-Form carry no novalidate, so an
 *     inaccurate max would block checkout with a native bubble.
 *
 * It paints only where the in-cart count is EXACTLY the recorded cap.
 * Below it the cap does not bind. Above it Shopify has allowed more than the
 * record claims, so the record is stale and the note would state a number
 * nobody is enforcing.
 *
 * This file NEVER writes or deletes a record. That is deliberate. The DOM is
 * not a cart read, it is a cache of one, and on /cart it is routinely stale:
 * the layout carries its own cart drawer copy of every line, so one variant
 * has two quantity inputs and the drawer's keeps the pre-422 count until
 * something repaints it. An earlier version deleted a record whenever an
 * input read below the cap, and that wiped a freshly recorded cap on every
 * /cart over-stock change that started below the cap, because the stale
 * drawer input was read first. Unknown must never mean drop. Only a
 * /cart.js read may expire a record, which is sbv-cart-sync.js's job.
 *
 * When it runs: DOMContentLoaded, a debounced MutationObserver on
 * cart-drawer and #main-cart-items, the sbv:cart-caps-changed event that
 * sbv-cart-sync fires after its own resync and reconciles, and any quantity
 * change. Idempotent: one note per line, never two, and every attribute it
 * sets is flagged so it can be handed back to the vendor untouched.
 */
(function () {
  'use strict';

  if (window.__sbvCartNoteBound) { return; }
  window.__sbvCartNoteBound = true;

  var CAPS_KEY = 'sbv-stock-caps';
  var CAPS_EVENT = 'sbv:cart-caps-changed';
  var NOTE_CLASS = 'sbv-stock-note';
  var DEBOUNCE = 50;
  var QTY_SELECTOR = 'input.quantity__input[data-quantity-variant-id]';

  var timer = null;

  function readCaps() {
    try {
      var raw = window.sessionStorage.getItem(CAPS_KEY);
      if (!raw) { return {}; }
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') { return {}; }
      return parsed;
    } catch (e) {
      return {};
    }
  }

  function toInt(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? null : n;
  }

  function closestEl(node, selector) {
    try {
      var el = node;
      while (el && el.nodeType === 1) {
        if (typeof el.matches === 'function' && el.matches(selector)) { return el; }
        el = el.parentNode;
      }
    } catch (e) { /* fall through */ }
    return null;
  }

  function inDrawer(input) {
    return !!closestEl(input, 'cart-drawer');
  }

  function noteText(cap) {
    // One sentence, no plural branch, no exclamation mark.
    return cap + ' available, the maximum for this order.';
  }

  function describedBy(input, id, add) {
    try {
      var raw = input.getAttribute('aria-describedby') || '';
      var parts = raw.split(/\s+/);
      var kept = [];
      var i;
      for (i = 0; i < parts.length; i++) {
        if (parts[i] && parts[i] !== id) { kept.push(parts[i]); }
      }
      if (add) { kept.push(id); }
      if (kept.length) {
        input.setAttribute('aria-describedby', kept.join(' '));
      } else {
        input.removeAttribute('aria-describedby');
      }
    } catch (e) { /* the note still reads, just without the association */ }
  }

  // The max and the aria-disabled are the vendor's attributes on loan. Both
  // are flagged and the previous value is kept, so clearing hands the line
  // back exactly as Liquid rendered it.
  function setCapAttributes(input, cap) {
    try {
      if (input.getAttribute('data-sbv-cap-max') !== '1') {
        input.setAttribute('data-sbv-prev-max', input.hasAttribute('max') ? input.getAttribute('max') : '');
        input.setAttribute('data-sbv-cap-max', '1');
      }
      input.setAttribute('max', String(cap));

      var plus = plusButton(input);
      if (plus && plus.getAttribute('data-sbv-cap-aria') !== '1') {
        plus.setAttribute('data-sbv-cap-aria', '1');
        plus.setAttribute('aria-disabled', 'true');
      }
    } catch (e) { /* attributes are the polish, the note is the point */ }
  }

  function clearCapAttributes(input) {
    try {
      if (input.getAttribute('data-sbv-cap-max') === '1') {
        var prev = input.getAttribute('data-sbv-prev-max');
        if (prev) {
          input.setAttribute('max', prev);
        } else {
          input.removeAttribute('max');
        }
        input.removeAttribute('data-sbv-prev-max');
        input.removeAttribute('data-sbv-cap-max');
      }

      var plus = plusButton(input);
      if (plus && plus.getAttribute('data-sbv-cap-aria') === '1') {
        plus.removeAttribute('aria-disabled');
        plus.removeAttribute('data-sbv-cap-aria');
      }
    } catch (e) { /* nothing to undo that matters */ }
  }

  function plusButton(input) {
    var wrap = closestEl(input, 'quantity-input');
    if (!wrap) { return null; }
    return wrap.querySelector('button[name="plus"]');
  }

  function makeNote(id, drawer, cap) {
    var note = document.createElement('div');
    note.className = NOTE_CLASS + (drawer ? ' ' + NOTE_CLASS + '--drawer' : ' ' + NOTE_CLASS + '--page');
    note.id = id;
    note.textContent = noteText(cap);
    return note;
  }

  // Drawer: a full-width row directly after the quantity and prices flex
  // row, so it is never squeezed into the left half of that row.
  // Cart page: inside the quantity cell, after the quantity wrapper and
  // before the vendor's red error slot, which is left alone.
  function insertNote(input, note, drawer) {
    if (drawer) {
      var row = closestEl(input, '.cart-drawer-item__quantity-and-prices');
      if (row && row.parentNode) {
        row.parentNode.insertBefore(note, row.nextSibling);
        return true;
      }
      var cell = closestEl(input, '.cart-item__quantity');
      if (cell) { cell.appendChild(note); return true; }
      return false;
    }

    var wrapper = closestEl(input, '.cart-item__quantity-wrapper');
    if (wrapper && wrapper.parentNode) {
      wrapper.parentNode.insertBefore(note, wrapper.nextSibling);
      return true;
    }
    var td = closestEl(input, '.cart-item__quantity');
    if (td) { td.appendChild(note); return true; }
    return false;
  }

  function render() {
    var caps, inputs;
    try {
      caps = readCaps();
      inputs = document.querySelectorAll(QTY_SELECTOR);
    } catch (e) {
      return;
    }

    var keep = {};
    var i;

    for (i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var variantId = String(input.getAttribute('data-quantity-variant-id') || '');
      var entry = Object.prototype.hasOwnProperty.call(caps, variantId) ? caps[variantId] : null;
      var cap = entry ? toInt(entry.cap) : null;

      if (!variantId || !entry || !cap || cap <= 0) {
        clearCapAttributes(input);
        continue;
      }

      // The in-cart count for this variant across every line, rendered by
      // Liquid as cart | item_count_for_variant on both surfaces.
      var count = toInt(input.getAttribute('data-cart-quantity'));
      if (count === null) { count = toInt(input.value); }
      if (count === null) { continue; }

      // THE RENDERER NEVER DELETES A RECORD. The DOM is not a cart read, it
      // is a cache of one, and on /cart it is routinely stale: the layout
      // carries its own cart drawer copy of the same line, so one variant
      // has two inputs and the drawer's still holds the pre-422 count until
      // something repaints it. Deleting on "this input says less than the
      // cap" wiped a freshly recorded cap on every /cart over-stock change
      // that started below the cap, because the drawer input was read first
      // and still said 9 (probe on staging, 2026-09-17). Unknown must never
      // mean drop. Only a /cart.js read may expire a record, which is
      // sbv-cart-sync.js's job.
      //
      // The note is painted only when the count is EXACTLY the cap. Below it
      // the cap does not bind. Above it Shopify has allowed more than the
      // record claims, so the record is stale and painting it would state a
      // number nobody is enforcing. A missing note is always acceptable; a
      // wrong number is not.
      if (count !== cap) {
        clearCapAttributes(input);
        continue;
      }

      var drawer = inDrawer(input);
      var index = input.getAttribute('data-index') || String(i + 1);
      var id = (drawer ? 'CartDrawer-StockNote-' : 'StockNote-') + index;

      // The two surfaces use different id prefixes, so an id can only ever
      // belong to this line. It is still checked against the line root, in
      // case a re-render moved the line indexes around.
      var lineRoot = closestEl(input, drawer ? 'li' : 'tr');
      var existing = document.getElementById(id);
      if (existing && lineRoot && !lineRoot.contains(existing)) {
        if (existing.parentNode) { existing.parentNode.removeChild(existing); }
        existing = null;
      }

      if (existing) {
        if (existing.textContent !== noteText(cap)) { existing.textContent = noteText(cap); }
      } else {
        var note = makeNote(id, drawer, cap);
        if (!insertNote(input, note, drawer)) { continue; }
        existing = note;
      }

      keep[id] = true;
      describedBy(input, id, true);

      // The count is exactly the cap here, which is the only state that
      // reaches this line, so the max is accurate. An inaccurate max would
      // block checkout with a native bubble: form#cart and #CartDrawer-Form
      // carry no novalidate.
      setCapAttributes(input, cap);
    }

    // Anything we rendered earlier that is no longer warranted goes, so a
    // Pro Pack swap or a vendor re-render can never leave a second note or
    // an orphan behind.
    try {
      var stale = document.querySelectorAll('.' + NOTE_CLASS);
      for (i = stale.length - 1; i >= 0; i--) {
        var el = stale[i];
        if (keep[el.id]) { continue; }
        var owner = closestEl(el, '.cart-item__quantity')
          || closestEl(el, '.cart-drawer-item__right')
          || closestEl(el, 'tr');
        if (owner) {
          var ownerInput = owner.querySelector(QTY_SELECTOR);
          if (ownerInput) { describedBy(ownerInput, el.id, false); }
        }
        if (el.parentNode) { el.parentNode.removeChild(el); }
      }
    } catch (e) { /* an orphan note is cosmetic, never worth throwing */ }

  }

  function schedule() {
    try {
      if (timer) { window.clearTimeout(timer); }
      timer = window.setTimeout(function () {
        timer = null;
        render();
      }, DEBOUNCE);
    } catch (e) {
      render();
    }
  }

  function observe() {
    try {
      if (typeof window.MutationObserver !== 'function') { return; }
      var targets = [];
      var drawer = document.querySelector('cart-drawer');
      var page = document.getElementById('main-cart-items');
      if (drawer) { targets.push(drawer); }
      if (page) { targets.push(page); }

      for (var i = 0; i < targets.length; i++) {
        if (targets[i].__sbvNoteObserved) { continue; }
        targets[i].__sbvNoteObserved = true;
        new window.MutationObserver(schedule).observe(targets[i], { childList: true, subtree: true });
      }
    } catch (e) { /* the event and the change listener still cover the common paths */ }
  }

  function start() {
    observe();
    render();
  }

  try {
    document.addEventListener(CAPS_EVENT, function () { observe(); schedule(); });
    document.addEventListener('change', function (event) {
      var target = event && event.target;
      if (target && target.classList && target.classList.contains('quantity__input')) { schedule(); }
    }, true);
  } catch (e) { /* listeners are best effort */ }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
